import React, { useState } from 'react';
import type { UserProfile } from '../../auth';
import cotagroLogoHorizontal from '../../../assets/COTAGRO_LOGO_HORIZONTAL.svg';
import { ConfirmationModal } from './ConfirmationModal';

const NAV_LINKS = [
  { id: 'home', label: 'Inicio', icon: 'home' },
  { id: 'productores', label: 'Productores', icon: 'users' },
  { id: 'historial', label: 'Historial', icon: 'history' },
  { id: 'configuracion', label: 'Ajustes', icon: 'settings' },
];

interface SidebarProps {
  activePage: string;
  onPageChange: (page: string) => void;
  isOpen: boolean;
  onLogout?: () => void;
  userProfile?: UserProfile | null;
}

export const Sidebar: React.FC<SidebarProps> = ({ activePage, onPageChange, isOpen, onLogout, userProfile }) => {
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  return (
    <aside 
      id="sidebar" 
      className={`sidebar w-64 h-screen border-r border-border flex-shrink-0 transition-all duration-300 z-40 relative
        ${isOpen ? 'fixed inset-y-0 left-0' : 'hidden md:block'}
      `}
      style={{ backgroundColor: '#E7F5E4' }}
    >
      <div className="p-6">
        <div className="flex justify-center mb-8">
          <div className="flex flex-col">
            <img src={cotagroLogoHorizontal} alt="Cotagro" className="h-10 w-auto object-contain" />
            <p className="text-[11px] text-muted-foreground font-medium -mt-1 text-left pl-1">Panel Admin</p>
          </div>
        </div>
        
        <nav className="space-y-2">
          {NAV_LINKS.map((link) => (
            <a 
              key={link.id}
              href="#" 
              onClick={(e) => {
                e.preventDefault();
                onPageChange(link.id);
              }}
              className={`nav-link flex items-center gap-3 px-3 py-2 rounded-lg ${
                activePage === link.id ? 'active-nav' : ''
              }`}
            >
              {renderIcon(link.icon)}
              {link.label}
            </a>
          ))}
        </nav>
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-6" style={{ borderTop: '1px solid rgba(0, 104, 55, 0.15)' }}>
        <button 
          onClick={() => onPageChange('perfil')}
          className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/60 transition text-left mb-2 group"
          title="Ver mi perfil"
        >
          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-semibold shadow-sm flex-shrink-0">
            {userProfile?.full_name?.charAt(0).toUpperCase() || 'A'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm truncate text-[#212121] group-hover:text-primary transition-colors">
              {userProfile?.full_name || 'Admin'}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {userProfile?.email || 'admin@cotagro.com'}
            </p>
          </div>
          <svg className="w-4 h-4 text-muted-foreground flex-shrink-0 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
        {onLogout && (
          <button 
            onClick={() => setIsLogoutModalOpen(true)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold border border-red-200 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Cerrar Sesión
          </button>
        )}
      </div>

      <ConfirmationModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={() => {
          setIsLogoutModalOpen(false);
          onLogout?.();
        }}
        title="¿Cerrar sesión?"
        description="¿Estás seguro de que querés salir de tu cuenta?"
        confirmText="Cerrar sesión"
        cancelText="Volver"
        type="danger"
      />
    </aside>
  );
};

function renderIcon(icon: string) {
  switch (icon) {
    case 'home':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
        </svg>
      );
    case 'users':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
        </svg>
      );
    case 'history':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      );
    case 'settings':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
        </svg>
      );
    default:
      return null;
  }
}
